/**
 * Additional excellent-fit industry SEO landings (data-driven).
 * Merged into INDUSTRY_SEO_PAGES — sitemap / middleware / routes pick them up automatically.
 */

export interface IndustrySeoPage {
  slug: string;
  keyword: string;
  title: string;
  description: string;
  keywords: string;
  h1: string;
  h1Accent: string;
  subheadline: string;
  heroImage: string;
  heroImageAlt: string;
  tradeLabel: string;
  features: Array<{ title: string; description: string }>;
  faqs: Array<{ q: string; a: string }>;
  relatedSlugs: string[];
  /** Sitemap lastmod. Omit to keep the stable corpus date. */
  updated?: string;
}

const BASE_KEYWORDS =
  'service app, field service app, small business software, ServiceApp, ServiceApp.ai, unlimited crew, WhatsApp quotes';

type TradeImage = 'cleaning' | 'hvac' | 'pest' | 'trades';

const IMAGE: Record<TradeImage, { src: string; altVerb: string }> = {
  cleaning: {
    src: '/serviceapp-owner-cleaning.jpg',
    altVerb: 'reviewing schedules, crews and invoices',
  },
  hvac: {
    src: '/serviceapp-owner-hvac.jpg',
    altVerb: 'checking jobs, technicians and revenue',
  },
  pest: {
    src: '/serviceapp-owner-pest.jpg',
    altVerb: 'tracking recurring work, follow-ups and revenue',
  },
  trades: {
    src: '/serviceapp-owner-trades.jpg',
    altVerb: 'reviewing quotes, jobs and outstanding invoices',
  },
};

interface IndustrySeed {
  slug: string;
  keyword: string;
  titleLead: string;
  descriptionLead: string;
  h1: string;
  h1Accent: string;
  subheadline: string;
  tradeLabel: string;
  image: TradeImage;
  features: Array<{ title: string; description: string }>;
  faqs: Array<{ q: string; a: string }>;
  relatedSlugs: string[];
  /** Extra SEO keyword phrases */
  keywordExtra?: string;
}

function buildPage(seed: IndustrySeed): IndustrySeoPage {
  const img = IMAGE[seed.image];
  return {
    slug: seed.slug,
    keyword: seed.keyword,
    title: `${seed.titleLead} — ServiceApp.ai`,
    description: `${seed.descriptionLead} Grow trial, unlimited crew, WhatsApp quotes, scheduling and payments.`,
    keywords: `${seed.keywordExtra ?? seed.keyword}, ${BASE_KEYWORDS}`,
    h1: seed.h1,
    h1Accent: seed.h1Accent,
    subheadline: seed.subheadline,
    heroImage: img.src,
    heroImageAlt: `${seed.tradeLabel} owner ${img.altVerb} in ServiceApp`,
    tradeLabel: seed.tradeLabel,
    features: seed.features,
    faqs: seed.faqs,
    relatedSlugs: seed.relatedSlugs,
  };
}

const DEFAULT_FEATURES = (trade: string): IndustrySeoPage['features'] => [
  {
    title: 'WhatsApp quotes with scope',
    description: `Send branded quotations with scope, exclusions and add-ons — ${trade.toLowerCase()} customers confirm into bookings faster.`,
  },
  {
    title: 'Job scheduling & dispatch',
    description: 'Drag-and-drop calendar, recurring contracts and live crew assignment on one screen.',
  },
  {
    title: 'Field / supervisor app',
    description: 'Crews see today’s jobs, send ETA, complete checklists and upload photos on any phone.',
  },
  {
    title: 'Customer portal',
    description: 'Customers book, track visits and pay online — fewer “where is my team?” calls.',
  },
  {
    title: 'Instant invoicing',
    description: 'Turn completed jobs into invoices and payment links the same day.',
  },
  {
    title: 'AI guidance & support',
    description: 'See where quotes, capacity and cash are slipping, and book a demo or setup call.',
  },
];

function defaultFaqs(trade: string, keyword: string): IndustrySeoPage['faqs'] {
  return [
    {
      q: `What is the best ${keyword} for small teams?`,
      a: `ServiceApp.ai is built for small ${trade.toLowerCase()} companies that quote work, schedule crews and invoice customers. Unlimited crew. Pay only for office users. Start from $49/mo · 2-month Grow trial.`,
    },
    {
      q: `Does ServiceApp work as ${keyword}?`,
      a: `Yes. ${trade} teams use ServiceApp for leads, WhatsApp quotes, confirmations, reminders, crew scheduling, job reports, invoicing and payments in one app.`,
    },
    {
      q: 'Is pricing per user?',
      a: 'You pay for office users only. Unlimited crew — field and crew logins stay free on every plan. Extra office seats can be added later; until then, upgrade to Grow.',
    },
  ];
}

const SEEDS: IndustrySeed[] = [
  {
    slug: '/electrical-business-software',
    keyword: 'electrical business software',
    titleLead: 'Electrical Business Software | Job Scheduling & Invoicing',
    descriptionLead:
      'Electrical business software for service calls, quotes, crew dispatch and invoicing. Run electrical jobs from enquiry to payment in one field service app.',
    h1: 'Electrical business software',
    h1Accent: 'quote, dispatch, get paid.',
    subheadline:
      'Log urgent calls, send WhatsApp quotes, schedule electricians and invoice on completion — electrical field service software your crew will actually open.',
    tradeLabel: 'Electrical',
    image: 'trades',
    features: DEFAULT_FEATURES('Electrical'),
    faqs: defaultFaqs('Electrical', 'electrical business software'),
    relatedSlugs: ['/plumbing-business-software', '/handyman-business-software', '/field-service-app'],
    keywordExtra: 'electrician business app, electrician scheduling software, electrical contractor software',
  },
  {
    slug: '/painting-business-software',
    keyword: 'painting business software',
    titleLead: 'Painting Business Software | Quotes, Crews & Invoicing',
    descriptionLead:
      'Painting business software for residential and commercial painters. Quote rooms and projects, schedule crews, track progress and get paid faster.',
    h1: 'Painting business software',
    h1Accent: 'for crews that finish clean.',
    subheadline:
      'Estimate with clear scope, schedule painting crews, capture before/after photos and invoice when the job is done — one app for painting contractors.',
    tradeLabel: 'Painting',
    image: 'trades',
    features: DEFAULT_FEATURES('Painting'),
    faqs: defaultFaqs('Painting', 'painting business software'),
    relatedSlugs: ['/interior-fitout-software', '/handyman-business-software', '/service-business-app'],
    keywordExtra: 'painting contractor software, painter scheduling app, painting estimate software',
  },
  {
    slug: '/appliance-repair-software',
    keyword: 'appliance repair software',
    titleLead: 'Appliance Repair Software | Dispatch & Invoicing',
    descriptionLead:
      'Appliance repair software for on-site technicians. Capture calls, quote repairs, dispatch techs and collect payment before you leave.',
    h1: 'Appliance repair software',
    h1Accent: 'fix call to cash.',
    subheadline:
      'Book service calls, send on-site quotes, complete job reports and get paid the same day — appliance field service software for growing teams.',
    tradeLabel: 'Appliance repair',
    image: 'trades',
    features: DEFAULT_FEATURES('Appliance repair'),
    faqs: defaultFaqs('Appliance repair', 'appliance repair software'),
    relatedSlugs: ['/handyman-business-software', '/hvac-service-software', '/field-service-software'],
  },
  {
    slug: '/water-tank-cleaning-software',
    keyword: 'water tank cleaning software',
    titleLead: 'Water Tank Cleaning Software | Scheduling & Reports',
    descriptionLead:
      'Water tank cleaning software for recurring residential and commercial contracts. Schedule visits, send reports and invoice automatically.',
    h1: 'Water tank cleaning software',
    h1Accent: 'for recurring contracts.',
    subheadline:
      'Run tank cleaning routes, capture photo proof, send service reports and invoice on completion — built for water tank cleaning companies.',
    tradeLabel: 'Water tank cleaning',
    image: 'cleaning',
    features: DEFAULT_FEATURES('Water tank cleaning'),
    faqs: defaultFaqs('Water tank cleaning', 'water tank cleaning software'),
    relatedSlugs: ['/cleaning-service-software', '/facility-management-software', '/pest-control-software'],
  },
  {
    slug: '/carpet-cleaning-software',
    keyword: 'carpet cleaning software',
    titleLead: 'Carpet Cleaning Software | Bookings & Invoicing',
    descriptionLead:
      'Carpet and upholstery cleaning software for booking, crew dispatch, checklists and same-day invoicing. Grow your carpet cleaning business with ServiceApp.ai.',
    h1: 'Carpet cleaning software',
    h1Accent: 'book, clean, get paid.',
    subheadline:
      'Take bookings online, schedule carpet and sofa cleans, complete room checklists and send payment links the same day.',
    tradeLabel: 'Carpet cleaning',
    image: 'cleaning',
    features: DEFAULT_FEATURES('Carpet cleaning'),
    faqs: defaultFaqs('Carpet cleaning', 'carpet cleaning software'),
    relatedSlugs: ['/cleaning-service-software', '/maid-service-software', '/cleaning-service-app'],
    keywordExtra: 'upholstery cleaning software, sofa cleaning business app',
  },
  {
    slug: '/pressure-washing-software',
    keyword: 'pressure washing software',
    titleLead: 'Pressure Washing Software | Quotes & Crew Scheduling',
    descriptionLead:
      'Pressure washing software for exterior cleaning contractors. Quote driveways and buildings, dispatch crews and invoice with photo proof.',
    h1: 'Pressure washing software',
    h1Accent: 'for exterior crews.',
    subheadline:
      'Win more exterior jobs with WhatsApp quotes, schedule pressure-washing crews and collect payment with before/after reports.',
    tradeLabel: 'Pressure washing',
    image: 'cleaning',
    features: DEFAULT_FEATURES('Pressure washing'),
    faqs: defaultFaqs('Pressure washing', 'pressure washing software'),
    relatedSlugs: [
      '/cleaning-service-software',
      '/window-cleaning-software',
      '/landscaping-business-software',
    ],
  },
  {
    slug: '/window-cleaning-software',
    keyword: 'window cleaning software',
    titleLead: 'Window Cleaning Software | Routes & Recurring Jobs',
    descriptionLead:
      'Window cleaning software for residential and commercial routes. Schedule recurring cleans, dispatch crews and invoice on completion.',
    h1: 'Window cleaning software',
    h1Accent: 'for route-based crews.',
    subheadline:
      'Plan window cleaning routes, manage recurring commercial contracts and get paid faster — window cleaning business software in one app.',
    tradeLabel: 'Window cleaning',
    image: 'cleaning',
    features: DEFAULT_FEATURES('Window cleaning'),
    faqs: defaultFaqs('Window cleaning', 'window cleaning software'),
    relatedSlugs: [
      '/cleaning-service-software',
      '/facility-management-software',
      '/pressure-washing-software',
    ],
  },
  {
    slug: '/pool-service-software',
    keyword: 'pool service software',
    titleLead: 'Pool Service Software | Routes, AMC & Invoicing',
    descriptionLead:
      'Pool and spa service software for recurring maintenance routes, chemical notes, crew dispatch and automatic invoicing.',
    h1: 'Pool service software',
    h1Accent: 'keep every route full.',
    subheadline:
      'Schedule pool maintenance visits, track service notes, remind customers and invoice on every stop — pool service business software for growing routes.',
    tradeLabel: 'Pool service',
    image: 'pest',
    features: DEFAULT_FEATURES('Pool service'),
    faqs: defaultFaqs('Pool service', 'pool service software'),
    relatedSlugs: [
      '/landscaping-business-software',
      '/facility-management-software',
      '/pest-control-software',
    ],
    keywordExtra: 'pool maintenance software, spa service scheduling app',
  },
  {
    slug: '/locksmith-business-software',
    keyword: 'locksmith business software',
    titleLead: 'Locksmith Business Software | Emergency Dispatch & Pay',
    descriptionLead:
      'Locksmith business software for emergency calls, on-site quotes, mobile dispatch and same-day payment collection.',
    h1: 'Locksmith business software',
    h1Accent: 'urgent calls, fast cash.',
    subheadline:
      'Log lockouts and installs, send ETA, complete the job and collect payment from the phone — locksmith field service software that keeps up.',
    tradeLabel: 'Locksmith',
    image: 'trades',
    features: DEFAULT_FEATURES('Locksmith'),
    faqs: defaultFaqs('Locksmith', 'locksmith business software'),
    relatedSlugs: ['/handyman-business-software', '/electrical-business-software', '/field-service-app'],
  },
  {
    slug: '/junk-removal-software',
    keyword: 'junk removal software',
    titleLead: 'Junk Removal Software | Book, Dispatch & Invoice',
    descriptionLead:
      'Junk removal software for haul-away companies. Take bookings, dispatch trucks, capture photos and get paid on completion.',
    h1: 'Junk removal software',
    h1Accent: 'fill every truck.',
    subheadline:
      'Quote by load, schedule pickups, send crews with live job details and invoice when the truck leaves — junk removal business software.',
    tradeLabel: 'Junk removal',
    image: 'trades',
    features: DEFAULT_FEATURES('Junk removal'),
    faqs: defaultFaqs('Junk removal', 'junk removal software'),
    relatedSlugs: ['/moving-company-software', '/handyman-business-software', '/service-app'],
  },
  {
    slug: '/garage-door-repair-software',
    keyword: 'garage door repair software',
    titleLead: 'Garage Door Repair Software | Service Calls & Invoices',
    descriptionLead:
      'Garage door and gate repair software for service calls, quotes, technician dispatch and payment links.',
    h1: 'Garage door repair software',
    h1Accent: 'service calls that convert.',
    subheadline:
      'Book repairs and installs, quote parts and labour, dispatch technicians and collect payment on site.',
    tradeLabel: 'Garage door & gates',
    image: 'trades',
    features: DEFAULT_FEATURES('Garage door repair'),
    faqs: defaultFaqs('Garage door & gate', 'garage door repair software'),
    relatedSlugs: ['/handyman-business-software', '/electrical-business-software', '/field-service-software'],
  },
  {
    slug: '/moving-company-software',
    keyword: 'moving company software',
    titleLead: 'Moving Company Software | Quotes, Crews & Payments',
    descriptionLead:
      'Moving company software for packers and movers. Quote moves, schedule crews, track jobs and invoice customers in one app.',
    h1: 'Moving company software',
    h1Accent: 'from estimate to unload.',
    subheadline:
      'Send clear move quotes, assign packing and transport crews, capture completion notes and get paid — movers software without enterprise bloat.',
    tradeLabel: 'Moving',
    image: 'trades',
    features: DEFAULT_FEATURES('Moving'),
    faqs: defaultFaqs('Moving', 'moving company software'),
    relatedSlugs: ['/junk-removal-software', '/handyman-business-software', '/service-business-app'],
    keywordExtra: 'packers and movers software, moving business app',
  },
  {
    slug: '/roofing-business-software',
    keyword: 'roofing business software',
    titleLead: 'Roofing Business Software | Quotes, Crews & Invoicing',
    descriptionLead:
      'Roofing business software for inspections, project quotes, crew scheduling, photo reports and invoicing.',
    h1: 'Roofing business software',
    h1Accent: 'win more roof jobs.',
    subheadline:
      'Inspect, quote with scope and add-ons, schedule roofing crews and invoice with photo proof — roofing contractor software for small and mid teams.',
    tradeLabel: 'Roofing',
    image: 'trades',
    features: DEFAULT_FEATURES('Roofing'),
    faqs: defaultFaqs('Roofing', 'roofing business software'),
    relatedSlugs: [
      '/waterproofing-software',
      '/handyman-business-software',
      '/field-service-management-software',
    ],
  },
  {
    slug: '/solar-installation-software',
    keyword: 'solar installation software',
    titleLead: 'Solar Installation Software | Site Visits, Crews & AMC',
    descriptionLead:
      'Solar installation and maintenance software for site surveys, install crews, commissioning and AMC follow-ups.',
    h1: 'Solar installation software',
    h1Accent: 'survey to service.',
    subheadline:
      'Quote solar installs, schedule site visits and install crews, log commissioning notes and run maintenance contracts in one app.',
    tradeLabel: 'Solar',
    image: 'hvac',
    features: DEFAULT_FEATURES('Solar'),
    faqs: defaultFaqs('Solar', 'solar installation software'),
    relatedSlugs: [
      '/electrical-business-software',
      '/hvac-service-software',
      '/facility-management-software',
    ],
    keywordExtra: 'solar maintenance software, solar contractor scheduling app',
  },
  {
    slug: '/fire-protection-software',
    keyword: 'fire protection service software',
    titleLead: 'Fire Protection Software | Inspections, AMC & Reports',
    descriptionLead:
      'Fire protection and extinguisher servicing software for scheduled inspections, compliance reports, crew dispatch and invoicing.',
    h1: 'Fire protection software',
    h1Accent: 'inspections on schedule.',
    subheadline:
      'Plan extinguisher and fire-safety inspections, capture service reports, remind customers and invoice every visit — fire protection field service software.',
    tradeLabel: 'Fire protection',
    image: 'pest',
    features: DEFAULT_FEATURES('Fire protection'),
    faqs: defaultFaqs('Fire protection', 'fire protection service software'),
    relatedSlugs: ['/facility-management-software', '/hvac-service-software', '/security-system-software'],
  },
  {
    slug: '/interior-fitout-software',
    keyword: 'interior fitout software',
    titleLead: 'Interior Fit-Out Software | Quotes, Crews & Snags',
    descriptionLead:
      'Interior fit-out software for joinery, partitions and fit-out contractors. Quote scope, schedule install crews, track snags and get paid by milestone or job.',
    h1: 'Interior fit-out software',
    h1Accent: 'from quote to handover.',
    subheadline:
      'Win fit-out jobs with detailed WhatsApp quotes, schedule carpenters and installers, capture progress photos and invoice cleanly — fit-out company software.',
    tradeLabel: 'Interior fit-out',
    image: 'trades',
    features: DEFAULT_FEATURES('Interior fit-out'),
    faqs: [
      ...defaultFaqs('Interior fit-out', 'interior fitout software'),
      {
        q: 'Is ServiceApp for design studios or fit-out execution teams?',
        a: 'It is strongest for fit-out and interior execution companies that dispatch install crews, manage site visits and invoice by job. Pure design-only studios are a weaker fit unless they also run installs.',
      },
    ],
    relatedSlugs: [
      '/interiors-business-software',
      '/painting-business-software',
      '/furniture-assembly-software',
    ],
    keywordExtra: 'fit out company software, joinery business software, fitout contractor app',
  },
  {
    slug: '/interiors-business-software',
    keyword: 'interior design install software',
    titleLead: 'Interiors Business Software | Design-Install Teams',
    descriptionLead:
      'Interiors business software for interior companies that design and install. Manage site visits, install schedules, snag lists and customer payments.',
    h1: 'Interiors business software',
    h1Accent: 'site visits to snag-free handover.',
    subheadline:
      'Coordinate measurements, installs and snag visits for interior companies — quotes, crew schedules, photo reports and payments in one ServiceApp workspace.',
    tradeLabel: 'Interiors',
    image: 'trades',
    features: DEFAULT_FEATURES('Interiors'),
    faqs: defaultFaqs('Interiors', 'interior design install software'),
    relatedSlugs: ['/interior-fitout-software', '/painting-business-software', '/false-ceiling-software'],
    keywordExtra: 'interior company software, interior contractor scheduling, interior execution software',
  },
  {
    slug: '/false-ceiling-software',
    keyword: 'false ceiling contractor software',
    titleLead: 'False Ceiling Software | Quotes & Install Crews',
    descriptionLead:
      'False ceiling and partition contractor software for estimates, install scheduling, site photos and invoicing.',
    h1: 'False ceiling software',
    h1Accent: 'measure, install, invoice.',
    subheadline:
      'Quote gypsum and partition jobs, schedule install crews, track site progress and get paid — false ceiling contractor software.',
    tradeLabel: 'False ceiling & partitions',
    image: 'trades',
    features: DEFAULT_FEATURES('False ceiling'),
    faqs: defaultFaqs('False ceiling', 'false ceiling contractor software'),
    relatedSlugs: [
      '/interior-fitout-software',
      '/interiors-business-software',
      '/electrical-business-software',
    ],
  },
  {
    slug: '/furniture-assembly-software',
    keyword: 'furniture assembly software',
    titleLead: 'Furniture Assembly Software | Delivery & Install Jobs',
    descriptionLead:
      'Furniture assembly and install software for delivery teams. Book installs, dispatch assemblers and collect payment on completion.',
    h1: 'Furniture assembly software',
    h1Accent: 'deliver, assemble, collect.',
    subheadline:
      'Schedule furniture delivery and assembly jobs, send crews with job details and invoice when the install is done.',
    tradeLabel: 'Furniture assembly',
    image: 'trades',
    features: DEFAULT_FEATURES('Furniture assembly'),
    faqs: defaultFaqs('Furniture assembly', 'furniture assembly software'),
    relatedSlugs: ['/moving-company-software', '/handyman-business-software', '/interior-fitout-software'],
  },
  {
    slug: '/waterproofing-software',
    keyword: 'waterproofing contractor software',
    titleLead: 'Waterproofing Software | Jobs, Photos & Invoices',
    descriptionLead:
      'Waterproofing contractor software for site surveys, treatment jobs, photo reports and invoicing.',
    h1: 'Waterproofing software',
    h1Accent: 'survey to sealed job.',
    subheadline:
      'Quote waterproofing treatments, schedule crews, capture photo proof and invoice on completion — waterproofing business software.',
    tradeLabel: 'Waterproofing',
    image: 'trades',
    features: DEFAULT_FEATURES('Waterproofing'),
    faqs: defaultFaqs('Waterproofing', 'waterproofing contractor software'),
    relatedSlugs: [
      '/roofing-business-software',
      '/painting-business-software',
      '/facility-management-software',
    ],
  },
  {
    slug: '/generator-maintenance-software',
    keyword: 'generator maintenance software',
    titleLead: 'Generator Maintenance Software | AMC & Breakdowns',
    descriptionLead:
      'Generator and UPS maintenance software for AMC contracts, breakdown dispatch, service reports and invoicing.',
    h1: 'Generator maintenance software',
    h1Accent: 'AMC without spreadsheet chaos.',
    subheadline:
      'Schedule generator servicing, dispatch technicians to breakdowns, log readings and invoice every visit.',
    tradeLabel: 'Generator & UPS',
    image: 'hvac',
    features: DEFAULT_FEATURES('Generator maintenance'),
    faqs: defaultFaqs('Generator maintenance', 'generator maintenance software'),
    relatedSlugs: [
      '/hvac-service-software',
      '/electrical-business-software',
      '/facility-management-software',
    ],
  },
  {
    slug: '/security-system-software',
    keyword: 'security system install software',
    titleLead: 'Security & CCTV Software | Install Jobs & Service',
    descriptionLead:
      'Security system and CCTV install software for surveys, install crews, service calls and invoicing.',
    h1: 'Security & CCTV software',
    h1Accent: 'install and service cleanly.',
    subheadline:
      'Quote CCTV and access-control installs, schedule technicians, complete service reports and get paid — security field service software.',
    tradeLabel: 'Security & CCTV',
    image: 'trades',
    features: DEFAULT_FEATURES('Security & CCTV'),
    faqs: defaultFaqs('Security & CCTV', 'security system install software'),
    relatedSlugs: [
      '/electrical-business-software',
      '/fire-protection-software',
      '/facility-management-software',
    ],
  },
  {
    slug: '/mobile-car-detailing-software',
    keyword: 'mobile car detailing software',
    titleLead: 'Mobile Car Detailing Software | Bookings & Routes',
    descriptionLead:
      'Mobile car detailing software for on-location bookings, detailer dispatch, reminders and online payments.',
    h1: 'Mobile car detailing software',
    h1Accent: 'book the driveway.',
    subheadline:
      'Take detailing bookings, send confirmations and reminders, dispatch detailers and collect payment on site.',
    tradeLabel: 'Mobile detailing',
    image: 'cleaning',
    features: DEFAULT_FEATURES('Mobile car detailing'),
    faqs: defaultFaqs('Mobile car detailing', 'mobile car detailing software'),
    relatedSlugs: ['/cleaning-service-software', '/pressure-washing-software', '/service-app'],
  },
  {
    slug: '/elevator-maintenance-software',
    keyword: 'elevator maintenance software',
    titleLead: 'Elevator Maintenance Software | AMC Visits & Reports',
    descriptionLead:
      'Elevator and lift maintenance software for scheduled AMC visits, breakdown calls, service reports and invoicing for SMB teams.',
    h1: 'Elevator maintenance software',
    h1Accent: 'keep AMC visits on track.',
    subheadline:
      'Plan lift maintenance contracts, dispatch technicians, capture service notes and invoice every visit — elevator field service software for small teams.',
    tradeLabel: 'Elevator maintenance',
    image: 'hvac',
    features: DEFAULT_FEATURES('Elevator maintenance'),
    faqs: defaultFaqs('Elevator maintenance', 'elevator maintenance software'),
    relatedSlugs: [
      '/facility-management-software',
      '/generator-maintenance-software',
      '/hvac-service-software',
    ],
  },
  {
    slug: '/lawn-care-software',
    keyword: 'lawn care software',
    titleLead: 'Lawn Care Software | Routes, Recurring Visits & Pay',
    descriptionLead:
      'Lawn care software for mowing routes, seasonal schedules, crew dispatch and automatic invoicing.',
    h1: 'Lawn care software',
    h1Accent: 'keep every route booked.',
    subheadline:
      'Build recurring lawn routes, remind customers, dispatch crews and invoice after each visit — lawn care business software.',
    tradeLabel: 'Lawn care',
    image: 'pest',
    features: DEFAULT_FEATURES('Lawn care'),
    faqs: defaultFaqs('Lawn care', 'lawn care software'),
    relatedSlugs: ['/landscaping-business-software', '/tree-care-software', '/pool-service-software'],
  },
  {
    slug: '/tree-care-software',
    keyword: 'tree care software',
    titleLead: 'Tree Care Software | Quotes, Crews & Job Photos',
    descriptionLead:
      'Tree care and arborist software for estimates, crew scheduling, on-site reports and invoicing.',
    h1: 'Tree care software',
    h1Accent: 'estimate to clean-up.',
    subheadline:
      'Quote tree work safely, schedule crews, capture job photos and get paid — tree care business software for growing teams.',
    tradeLabel: 'Tree care',
    image: 'pest',
    features: DEFAULT_FEATURES('Tree care'),
    faqs: defaultFaqs('Tree care', 'tree care software'),
    relatedSlugs: ['/landscaping-business-software', '/lawn-care-software', '/junk-removal-software'],
  },
];

export const EXTRA_INDUSTRY_SEO_PAGES: IndustrySeoPage[] = SEEDS.map(buildPage);

/** Homepage + /industries cards for excellent-fit verticals (existing + new). */
export const EXCELLENT_FIT_INDUSTRY_CARDS: Array<{
  name: string;
  description: string;
  seoPath: string;
}> = [
  {
    name: 'Cleaning companies',
    description: 'Residential and commercial cleaning — recurring jobs, checklists and same-day invoicing.',
    seoPath: '/cleaning-service-software',
  },
  {
    name: 'Maid & housekeeping',
    description: 'Recurring home cleans, maid scheduling and online payments.',
    seoPath: '/maid-service-software',
  },
  {
    name: 'Janitorial',
    description: 'Multi-site commercial cleaning contracts, SLAs and crew dispatch.',
    seoPath: '/janitorial-software',
  },
  {
    name: 'Pest control',
    description: 'Treatment records, scheduled follow-ups and compliant service reports.',
    seoPath: '/pest-control-software',
  },
  {
    name: 'AC & HVAC',
    description: 'AMC contracts, scheduled servicing and urgent breakdown call management.',
    seoPath: '/hvac-service-software',
  },
  {
    name: 'Plumbing',
    description: 'Emergency calls, job costing and payment before you leave the site.',
    seoPath: '/plumbing-business-software',
  },
  {
    name: 'Electrical',
    description: 'Service calls, quotes, electrician dispatch and instant invoicing.',
    seoPath: '/electrical-business-software',
  },
  {
    name: 'Handyman services',
    description: 'Quote on the spot, flexible multi-trade jobs and collect payment fast.',
    seoPath: '/handyman-business-software',
  },
  {
    name: 'Facility management',
    description: 'Multi-site contracts with team dispatch, SLAs and consolidated billing.',
    seoPath: '/facility-management-software',
  },
  {
    name: 'Landscaping',
    description: 'Seasonal schedules, recurring visits and route-friendly crew planning.',
    seoPath: '/landscaping-business-software',
  },
  {
    name: 'Lawn care',
    description: 'Mowing routes, recurring visits and automatic invoicing.',
    seoPath: '/lawn-care-software',
  },
  {
    name: 'Tree care',
    description: 'Estimates, crew scheduling and photo reports for arborist teams.',
    seoPath: '/tree-care-software',
  },
  {
    name: 'Painting',
    description: 'Project quotes, painting crews, progress photos and invoicing.',
    seoPath: '/painting-business-software',
  },
  {
    name: 'Interior fit-out',
    description: 'Fit-out quotes, install crews, snags and milestone payments.',
    seoPath: '/interior-fitout-software',
  },
  {
    name: 'Interiors',
    description: 'Interior companies that measure, install and hand over snag-free.',
    seoPath: '/interiors-business-software',
  },
  {
    name: 'False ceiling',
    description: 'Gypsum and partition estimates, install scheduling and invoices.',
    seoPath: '/false-ceiling-software',
  },
  {
    name: 'Furniture assembly',
    description: 'Delivery and assembly jobs with crew dispatch and payments.',
    seoPath: '/furniture-assembly-software',
  },
  {
    name: 'Appliance repair',
    description: 'On-site repair calls, quotes, dispatch and same-day payment.',
    seoPath: '/appliance-repair-software',
  },
  {
    name: 'Water tank cleaning',
    description: 'Recurring tank contracts, photo proof and service reports.',
    seoPath: '/water-tank-cleaning-software',
  },
  {
    name: 'Carpet cleaning',
    description: 'Bookings, room checklists and same-day carpet cleaning invoices.',
    seoPath: '/carpet-cleaning-software',
  },
  {
    name: 'Pressure washing',
    description: 'Exterior quotes, crew dispatch and before/after photo reports.',
    seoPath: '/pressure-washing-software',
  },
  {
    name: 'Window cleaning',
    description: 'Route-based residential and commercial window cleaning.',
    seoPath: '/window-cleaning-software',
  },
  {
    name: 'Pool service',
    description: 'Recurring pool routes, maintenance notes and automatic invoicing.',
    seoPath: '/pool-service-software',
  },
  {
    name: 'Locksmith',
    description: 'Emergency dispatch, on-site quotes and mobile payment collection.',
    seoPath: '/locksmith-business-software',
  },
  {
    name: 'Junk removal',
    description: 'Book pickups, dispatch trucks and invoice on completion.',
    seoPath: '/junk-removal-software',
  },
  {
    name: 'Garage door & gates',
    description: 'Service calls, parts quotes and technician dispatch.',
    seoPath: '/garage-door-repair-software',
  },
  {
    name: 'Moving companies',
    description: 'Move quotes, packer/mover crews and payment collection.',
    seoPath: '/moving-company-software',
  },
  {
    name: 'Roofing',
    description: 'Inspections, roofing quotes, crews and photo-backed invoices.',
    seoPath: '/roofing-business-software',
  },
  {
    name: 'Solar',
    description: 'Site surveys, install crews and solar AMC maintenance.',
    seoPath: '/solar-installation-software',
  },
  {
    name: 'Fire protection',
    description: 'Inspection schedules, compliance reports and AMC invoicing.',
    seoPath: '/fire-protection-software',
  },
  {
    name: 'Waterproofing',
    description: 'Surveys, treatment jobs, photo proof and invoicing.',
    seoPath: '/waterproofing-software',
  },
  {
    name: 'Generator & UPS',
    description: 'AMC servicing, breakdown dispatch and service reports.',
    seoPath: '/generator-maintenance-software',
  },
  {
    name: 'Security & CCTV',
    description: 'Install surveys, technician scheduling and service calls.',
    seoPath: '/security-system-software',
  },
  {
    name: 'Mobile detailing',
    description: 'Driveway bookings, detailer dispatch and online payments.',
    seoPath: '/mobile-car-detailing-software',
  },
  {
    name: 'Elevator maintenance',
    description: 'Lift AMC visits, breakdown calls and compliance notes.',
    seoPath: '/elevator-maintenance-software',
  },
];

/** Grouped hub sections — interiors/fit-out near the top so they are easy to find. */
export const INDUSTRY_HUB_GROUPS: Array<{
  id: string;
  title: string;
  description: string;
  seoPaths: string[];
}> = [
  {
    id: 'cleaning',
    title: 'Cleaning & facilities',
    description: 'Recurring cleans, commercial contracts and specialty cleaning.',
    seoPaths: [
      '/cleaning-service-software',
      '/maid-service-software',
      '/janitorial-software',
      '/facility-management-software',
      '/carpet-cleaning-software',
      '/pressure-washing-software',
      '/window-cleaning-software',
      '/water-tank-cleaning-software',
    ],
  },
  {
    id: 'interiors',
    title: 'Interiors & fit-out',
    description: 'Fit-out, interiors, false ceiling, furniture and painting crews.',
    seoPaths: [
      '/interior-fitout-software',
      '/interiors-business-software',
      '/false-ceiling-software',
      '/furniture-assembly-software',
      '/painting-business-software',
    ],
  },
  {
    id: 'trades',
    title: 'Core trades',
    description: 'HVAC, plumbing, electrical, handyman and appliance repair.',
    seoPaths: [
      '/hvac-service-software',
      '/plumbing-business-software',
      '/electrical-business-software',
      '/handyman-business-software',
      '/appliance-repair-software',
      '/pest-control-software',
    ],
  },
  {
    id: 'outdoor',
    title: 'Outdoor & routes',
    description: 'Landscaping, lawn, tree care and on-site detailing.',
    seoPaths: [
      '/landscaping-business-software',
      '/lawn-care-software',
      '/tree-care-software',
      '/mobile-car-detailing-software',
      '/junk-removal-software',
    ],
  },
  {
    id: 'install-amc',
    title: 'Install, AMC & specialty',
    description: 'Roofing, solar, fire, waterproofing, generators, security and elevators.',
    seoPaths: [
      '/roofing-business-software',
      '/solar-installation-software',
      '/fire-protection-software',
      '/waterproofing-software',
      '/generator-maintenance-software',
      '/security-system-software',
      '/elevator-maintenance-software',
      '/pool-service-software',
    ],
  },
];
